import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '../../../context/AuthContext';
import { HEADER_NAV, currentProps, marginNotesShown } from './navItems';
import { DealsLink, HEADER_LINE, HEADER_TEXT, MarginNotes } from './headerParts';
import AccountMenu from './AccountMenu';
import GetStartedMenu from './GetStartedMenu';
import MobileMenu from './MobileMenu';
import useActiveClaimsCount from './useActiveClaimsCount';

// The header's breakpoints are em media queries, written as Tailwind arbitrary variants:
// in a media query 1em is the visitor's default font size (16px unless they enlarged it),
// so with larger text the bar turns into the mobile bar (and drops words) before anything
// is pushed off screen. The site's px screens (`nav:` = 800px) do not scale with text, and
// Tailwind's min-[]/max-[] variants refuse em values next to px screens. At 16px:
//   [@media(max-width:20.4375em)]  <= 327px  "Get started" shows its arrow only (label read by screen readers)
//   [@media(max-width:22.4375em)]  <= 359px  the reference's tight spacing (6px gaps), smaller logo and deals pin
//   [@media(min-width:22.5em)_and_(max-width:26.875em)]  360–430px  8px gaps, 72px logo (64px below 360px)
//   [@media(max-width:31.1875em)]  <= 499px  the Menu toggle shows its icon only (room for the deals pin)
//   [@media(min-width:26.9375em)]  >= 431px  20px sides instead of 12px
//   [@media(min-width:50em)]       >= 800px  the desktop bar (DESKTOP_QUERY)
//   [@media(min-width:64em)]       >= 1024px room for a longer name in the account button
//   [@media(min-width:71.9375em)]  >= 1151px wider gaps; the margin notes show
//   [@media(min-width:90.0625em)]  >= 1441px the 1400px cap
// Tailwind orders arbitrary variants as strings: keep two-digit values (string order =
// numeric order) when two of them set the same property.
export const DESKTOP_QUERY = '(min-width: 50em)';

/**
 * The fixed, floating bar (the reference's .site-header): 17px from the top and centred
 * from 800px (content width, at most the viewport minus 40px, 1400px from 1441px); below
 * 800px full width with 20px sides (12px up to 430px), 12px from the top. `w-max` from
 * 800px: with `left: 50%` a shrink-to-fit bar may only use the right half of the viewport,
 * which squeezed the reference's own bar below ~1320px ("Get started" spilled past its
 * edge); max-content keeps everything inside and the translate still centres it.
 * (Breakpoints: 800px etc. at the default 16px text, see the table above.)
 */
const BAR = cn(
  'fixed right-3 left-3 top-3 z-50 flex items-center justify-between whitespace-nowrap border text-navy',
  'gap-[14px] bg-brand-sheet/[0.96] py-2 pl-[13px] pr-[9px] backdrop-blur-[14px]',
  'shadow-[3px_3px_0_theme(colors.navy.DEFAULT/4%)]',
  '[@media(max-width:22.4375em)]:gap-1.5 [@media(max-width:22.4375em)]:pl-2.5',
  // 360–430px: 8px gaps, so "Get started" keeps its whole label beside the deals pin.
  '[@media(min-width:22.5em)_and_(max-width:26.875em)]:gap-2',
  '[@media(min-width:26.9375em)]:left-5 [@media(min-width:26.9375em)]:right-5',
  '[@media(min-width:50em)]:right-auto [@media(min-width:50em)]:left-1/2 [@media(min-width:50em)]:top-[17px]',
  '[@media(min-width:50em)]:w-max [@media(min-width:50em)]:max-w-[calc(100%-40px)] [@media(min-width:50em)]:-translate-x-1/2',
  '[@media(min-width:50em)]:justify-start [@media(min-width:50em)]:gap-5 [@media(min-width:50em)]:pl-[19px]',
  '[@media(min-width:71.9375em)]:gap-[27px] [@media(min-width:90.0625em)]:max-w-[1400px]',
  HEADER_LINE,
);

/**
 * The redesigned site header (approved Sept 26 2026), shown by components/Header.jsx when
 * SLOT_MODEL is on: logo, How it works · Businesses · Venues · Pricing, the account area
 * (AccountMenu, 800px+ at 16px text), "Find deals near me" (DealsLink: "Deals" from 800px, the
 * full label from 1151px, the pin alone on phones), "Get started →" (GetStartedMenu: the audience chooser
 * popover, every width; upgrade brief §2.1), the "Menu" toggle and panel (MobileMenu, below
 * 800px; the same three audience rows at its top), and the two margin notes at the top of
 * the page (1151px+, not on the pages in MARGIN_NOTE_HIDDEN_PATHS).
 *
 * Production entry points kept: Log in / Sign up (AuthModal), the account menu with the
 * role dashboards (lib/roles.js), Deal wallet with the active-codes badge
 * (GET /deals/my-claims, lib/claimStatus) and Log out (→ "/").
 *
 * Renders nothing on /admin*. No entrance animation: PublicLayout remounts it on every
 * path. Menus close on navigation, on sign-in/out and when the 800px breakpoint is crossed;
 * at most one of the three (account, chooser, Menu) is open at a time.
 */
export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout, openAuthModal } = useAuth();
  const activeClaims = useActiveClaimsCount(isAuthenticated, user?.id);
  const barRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const { pathname } = location;

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setAccountOpen(false);
    setChooserOpen(false);
  }, []);

  // Opening one menu closes the others (the Menu panel treats the whole bar as "inside",
  // so a tap on "Get started" would not dismiss it on its own).
  const only = useCallback((setOpen) => (next) => {
    if (next) closeAll();
    setOpen(next);
  }, [closeAll]);
  const onMenuChange = useMemo(() => only(setMenuOpen), [only]);
  const onAccountChange = useMemo(() => only(setAccountOpen), [only]);
  const onChooserChange = useMemo(() => only(setChooserOpen), [only]);

  // Any navigation (a same-path link or a #hash too) and any change of session closes both.
  useEffect(() => {
    closeAll();
  }, [location.key, isAuthenticated, closeAll]);

  // The two menus live on opposite sides of the 800px switch: crossing it closes them.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => closeAll();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [closeAll]);

  // The open mobile panel covers the page: the page behind it does not scroll.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const { body } = document;
    const previous = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previous;
    };
  }, [menuOpen]);

  const handleLogin = () => {
    closeAll();
    openAuthModal('login');
  };
  const handleSignup = () => {
    closeAll();
    openAuthModal('signup');
  };
  const handleLogout = () => {
    closeAll();
    logout();
    navigate('/');
  };

  // Hide the site header in the admin area (as the legacy header).
  if (pathname.startsWith('/admin')) return null;

  return (
    // The <header> (banner landmark) holds the fixed bar and the absolutely placed margin
    // notes; it takes no space itself. `site-light`: the rust focus ring on sheet and paper.
    // Not printed (as the reference: a fixed bar would repeat on every printed page).
    <header className="site-light print:hidden" data-site-header="">
      {/* Open, the bar and its panel (Menu or chooser) go above the patron bottom nav (z-50)
          and the cookie banner (z-[70]), still below the AuthModal (z-[100]). */}
      <div ref={barRef} className={cn(BAR, (menuOpen || chooserOpen) && 'z-[80]')}>
        {/* Only if the bar still overflows (text enlarged without the em queries noticing,
            or a screen under ~260px) does the logo give way, before the controls. */}
        <Link to="/" aria-label="Tappd home" className="block min-w-[40px] shrink-[3]">
          <img
            src="/tappd-logo.png"
            alt="Tappd"
            width="114"
            height="39"
            className="block h-auto w-[91px] max-w-full [@media(max-width:22.4375em)]:w-[64px] [@media(min-width:22.5em)_and_(max-width:26.875em)]:w-[72px] [@media(min-width:50em)]:w-[103px]"
          />
        </Link>

        {/* When the text is enlarged the links wrap onto a second line rather than push
            "Get started" off the bar (the list never shrinks below its widest link). */}
        <nav aria-label="Main" className="hidden [@media(min-width:50em)]:block">
          <ul className="m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-0 p-0 [@media(min-width:71.9375em)]:gap-x-6">
            {HEADER_NAV.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className={HEADER_TEXT} {...currentProps(pathname, item.to)}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <AccountMenu
          user={user}
          isAuthenticated={isAuthenticated}
          loading={Boolean(loading)}
          activeClaims={activeClaims}
          pathname={pathname}
          open={accountOpen}
          onOpenChange={onAccountChange}
          onLogin={handleLogin}
          onLogout={handleLogout}
        />

        {/* The guest entry point next to the audience chooser (owner 2026-09-28). */}
        <DealsLink pathname={pathname} />

        <GetStartedMenu pathname={pathname} open={chooserOpen} onOpenChange={onChooserChange} />

        <MobileMenu
          pathname={pathname}
          user={user}
          isAuthenticated={isAuthenticated}
          loading={Boolean(loading)}
          activeClaims={activeClaims}
          open={menuOpen}
          onOpenChange={onMenuChange}
          containerRef={barRef}
          onLogin={handleLogin}
          onSignup={handleSignup}
          onLogout={handleLogout}
        />
      </div>
      {marginNotesShown(pathname) && <MarginNotes />}
    </header>
  );
}
